import {
  CashLine,
  ListCheck,
  ListIdentLine,
  SearchIcon,
} from 'app/components/SvgAssets';
import TopNavigationMenu from 'app/operator/components/menu/TopNavigationMenu';
import {
  getDropdownIconClassNames,
  getRequestStatusClassNames,
  getTabClassNames,
} from 'app/operator/components/ClassUtils';
import { useFetchMarketOrders } from 'app/hooks/requests/dashboard';
import ProfileNavigationMenu from 'app/operator/components/menu/ProfileNavigationMenu';
import {
  currencyFormatter,
  getFirstItem,
  OutletTypeEnum,
} from 'app/utilities/helpers';
import { publicRoutes } from 'app/utilities/routes';
import { Request, RequestTypes } from 'app/utilities/types/shared';
import Dropdown from 'assets/images/dropdown.svg';
import { truncate } from 'lodash';
import { DateTime } from 'luxon';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { ElrButton, ElrButtonDropdown, ElrPageTitle } from 'ui-components';

interface Props {}

/* eslint-disable-next-line no-shadow */
enum TabNames {
  NEW_ORDERS = 'New Orders',
  ONGOING = 'Ongoing',
  COMPLETED = 'Completed',
}

const DashboardContainer: React.FC<Props> = () => {
  const [activeTab, setActiveTab] = useState(TabNames.NEW_ORDERS);
  const [underlinePosition, setUnderlinePosition] = useState({
    left: 0,
    width: 0,
  });
  const newOrdersTabRef = useRef<HTMLButtonElement | null>(null);
  const ongoingTabRef = useRef<HTMLButtonElement | null>(null);
  const completedTabRef = useRef<HTMLButtonElement | null>(null);
  const [isExpanded, setIsExpanded] = useState<number | null>(null);

  const { latestData: newOrdersData, isLoading: isLoadingNewOrdersRequests } =
    useFetchMarketOrders({
      outletType: OutletTypeEnum.market,
      status: RequestTypes.pending,
    });

  const {
    latestData: ongoingRequestsData,
    isLoading: isLoadingOngoingRequests,
  } = useFetchMarketOrders({
    outletType: OutletTypeEnum.market,
    status: RequestTypes.accepted,
  });

  const {
    latestData: completedRequestData,
    isLoading: isLoadingCompletedRequests,
  } = useFetchMarketOrders({
    outletType: OutletTypeEnum.market,
    status: RequestTypes.completed,
  });

  const toggleRow = (index: number) => {
    setIsExpanded(isExpanded === index ? null : index);
  };

  const tabs = [
    { name: TabNames.NEW_ORDERS, ref: newOrdersTabRef },
    { name: TabNames.ONGOING, ref: ongoingTabRef },
    { name: TabNames.COMPLETED, ref: completedTabRef },
  ];

  useEffect(() => {
    const activeTabRef = tabs.find((tab) => tab.name === activeTab)?.ref
      .current;
    if (activeTabRef) {
      const activeTabWidth = activeTabRef.offsetWidth;
      setUnderlinePosition({
        left: activeTabRef.offsetLeft + (activeTabWidth - 110) / 2,
        width: 110,
      });
    }
  }, [activeTab]);

  const newOrdersRequests =
    newOrdersData?.docs.map((request: Request) => ({
      ...request,
      status:
        request.status === RequestTypes.pending
          ? RequestTypes.pending
          : request.status,
    })) || [];

  const ongoingRequests =
    ongoingRequestsData?.docs.map((request: Request) => ({
      ...request,
      status:
        request.status === RequestTypes.accepted
          ? RequestTypes.accepted
          : request.status,
    })) || [];

  const completedRequests =
    completedRequestData?.docs.map((request: Request) => ({
      ...request,
      status:
        request.status === RequestTypes.completed
          ? RequestTypes.completed
          : request.status,
    })) || [];

  const filteredDocuments = (() => {
    switch (activeTab) {
      case TabNames.NEW_ORDERS:
        return newOrdersRequests;
      case TabNames.ONGOING:
        return ongoingRequests;
      case TabNames.COMPLETED:
        return completedRequests;
      default:
        return [];
    }
  })();

  const cardData = [
    {
      title: 'New Orders',
      count: newOrdersData?.totalDocs || 0,
      Icon: <ListIdentLine />,
    },
    {
      title: 'Total Sales',
      count: ongoingRequestsData?.totalDocs || 0,
      Icon: <CashLine />,
    },
    {
      title: 'Completed Orders',
      count: completedRequestData?.totalDocs || 0,
      Icon: <ListCheck />,
    },
  ];

  const _renderLoadState = () => {
    const isLoading =
      isLoadingNewOrdersRequests ||
      isLoadingOngoingRequests ||
      isLoadingCompletedRequests;

    if (isLoading) {
      return (
        <tr>
          <td
            colSpan={4}
            className="text-center italic py-8 text-elr-black-300"
          >
            Loading...
          </td>
        </tr>
      );
    }

    if (!filteredDocuments.length) {
      const emptyMessages: Record<string, React.ReactNode> = {
        [TabNames.NEW_ORDERS]: (
          <>
            There are no new requests.&nbsp;
            <span>
              Create a&nbsp;
              <a
                href={publicRoutes.estimate}
                className="cursor-pointer underline"
                target="_blank"
                rel="noreferrer"
              >
                new order here
              </a>
            </span>
          </>
        ),
        [TabNames.ONGOING]: <>There are no ongoing orders at the moment.</>,
        [TabNames.COMPLETED]: <>No completed order have been made yet.</>,
      };

      return (
        <tr>
          <td
            colSpan={4}
            className="text-center italic py-8 text-elr-black-300"
          >
            {emptyMessages[activeTab] || 'No data available.'}
          </td>
        </tr>
      );
    }
    return null;
  };

  return (
    <div>
      <ElrPageTitle title="Errandlr - Market Runs" />
      <ProfileNavigationMenu>
        <div className="flex justify-between w-full md:mx-auto items-center">
          <TopNavigationMenu containerClass="md:mr-0" />
        </div>
        <div className="bg-elr-gray flex flex-col py-8 px-7 md:pt-3 md:px-20">
          <div className="mb-6 text-elr-black font-normal font-serif leading-tight">
            <h3 className="pt-6 text-xl mb-2">Market Runs</h3>
            <p className="text-elr-black-300 text-base">
              View and manage market orders
            </p>
          </div>
          <DashboardCards data={cardData} />

          <section className="bg-elr-white-400 mb-8">
            <div className="py-4 md:px-8 px-2 md:flex justify-between border-b-2 border-elr-gray">
              <div className="flex self-center text-lg md:gap-16 md:justify-between justify-around border-elr-black-200 relative">
                {tabs.map((tab) => (
                  <button
                    key={tab.name}
                    type="button"
                    ref={tab.ref}
                    className={getTabClassNames(activeTab === tab.name)}
                    onClick={() => setActiveTab(tab.name)}
                  >
                    {tab.name}
                  </button>
                ))}
                <div
                  className={`rounded-t-lg h-1 bg-elr-black absolute transition-all duration-300 ease-in-out`}
                  style={{
                    width: `${underlinePosition.width}px`,
                    left: `${underlinePosition.left}px`,
                    bottom: '-24px',
                  }}
                />
              </div>
              <div className="flex gap-4 md:flex-row flex-col">
                <div className="md:flex justify-between gap-4 bg-elr-gray px-4 py-2 rounded-sm w-72 hidden">
                  <input
                    type="text"
                    placeholder="search address or tracking ID"
                    className="outline-none w-full bg-elr-gray border-none focus:outline-0"
                  />
                  <SearchIcon />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto w-full relative">
              <table className="table-auto w-full">
                <thead>
                  <tr className="border-b-2 border-elr-gray text-elr-black">
                    <th className="font-normal py-7 text-left md:pl-32 md:w-1/3  pl-6 flex-grow truncate">
                      Items
                    </th>
                    <th className="font-normal py-7 text-center flex-grow">
                      Customer
                    </th>
                    {(activeTab === TabNames.NEW_ORDERS ||
                      activeTab === TabNames.COMPLETED) && (
                      <th className="font-normal py-7 text-center flex-grow">
                        Tracking ID
                      </th>
                    )}
                    <th className="font-normal py-7 text-center flex-grow">
                      Amount
                    </th>
                    {activeTab === TabNames.ONGOING && (
                      <th className="font-normal py-7 text-center flex-grow">
                        Status
                      </th>
                    )}
                    <th className="font-normal py-7 text-center w-24" />
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.length
                    ? filteredDocuments.map(
                        (request: Request, index: number) => (
                          <Fragment key={request.trackingId}>
                            <TableRow
                              request={request}
                              index={index}
                              toggleRow={toggleRow}
                              isExpanded={isExpanded}
                              activeTab={activeTab}
                            />
                            {isExpanded === index && (
                              <ExpandedRow
                                request={request}
                                activeTab={activeTab}
                              />
                            )}
                          </Fragment>
                        )
                      )
                    : _renderLoadState()}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </ProfileNavigationMenu>
    </div>
  );
};

export default DashboardContainer;

interface CardData {
  id?: string;
  title: string;
  count: number;
  Icon: React.ReactNode;
}

interface DashboardCardsProps {
  data: CardData[];
}

export const DashboardCards: React.FC<DashboardCardsProps> = ({ data }) => (
  <section className="grid md:grid-cols-column-three grid-cols-column gap-5 mb-5">
    {data.map((card) => (
      <div key={card.title} className="bg-elr-white-400 py-5 px-6">
        <div>
          <div className="rounded-full p-2 inline-block">{card.Icon}</div>
          <div className="mb-5" />
          <p className="text-lg text-elr-black-300 leading-5 mb-3">
            {card.title}
          </p>
          <p className="text-3xl text-elr-black">{card.count}</p>
        </div>
      </div>
    ))}
  </section>
);

interface TableRowProps {
  request: Request;
  index: number;
  toggleRow: (index: number) => void;
  isExpanded: number | null;
  activeTab: TabNames;
}

export const TableRow: React.FC<TableRowProps> = ({
  request,
  index,
  toggleRow,
  isExpanded,
  activeTab,
}) => {
  const {
    pickupAddress,
    trackingId,
    priceEstimate,
    senderInformation,
    status,
  } = request;

  const formatDateTime = (createdAt: string) => {
    const dateTime = DateTime.fromISO(createdAt);
    return dateTime.toFormat('yyyy-LL-dd');
  };

  return (
    <tr
      className="text-center border border-x-0 border-elr-gray-500 cursor-pointer"
      onClick={() => toggleRow(index)}
    >
      <td className="text-left py-5 pl-7 text-elr-black truncate max-w-[150px] md:max-w-xs overflow-hidden">
        <div className="truncate">{pickupAddress?.fullAddress || 'N/A'}</div>
        <div className="text-elr-black-300 text-sm pt-1">
          {formatDateTime(request.createdAt)}
        </div>
      </td>
      <td className="py-5 px-5 text-center text-elr-black text-opacity-60 truncate">
        {truncate(senderInformation?.data?.name || 'N/A', { length: 20 })}
      </td>
      {(activeTab === TabNames.NEW_ORDERS ||
        activeTab === TabNames.COMPLETED) && (
        <td className="py-5 text-center text-elr-black text-opacity-60">
          {trackingId || 'N/A'}
        </td>
      )}
      <td className="py-5 px-5 text-center text-elr-black text-opacity-60">
        {currencyFormatter(priceEstimate)}
      </td>
      {activeTab === TabNames.ONGOING && (
        <td className="text-center text-elr-black text-opacity-60">
          <p className={getRequestStatusClassNames(status)}>
            {status === RequestTypes.accepted ? 'Assigned' : 'Picked Up'}
          </p>
        </td>
      )}
      <td className="py-5 relative">
        <button className="focus:outline-none" type="button">
          <img
            src={Dropdown}
            alt="Dropdown"
            className={getDropdownIconClassNames(isExpanded === index)}
          />
        </button>
      </td>
    </tr>
  );
};

interface ExpandedRowProps {
  request: Request;
  activeTab: TabNames;
}
export const ExpandedRow: React.FC<ExpandedRowProps> = ({
  request,
  activeTab,
}) => {
  const {
    deliverToInformation,
    senderInformation,
    pickupAddress,
    designatedAgent,
  } = request;
  const recipientInfo = getFirstItem(deliverToInformation);
  const riderName =
    designatedAgent?.firstname || designatedAgent?.lastname
      ? `${designatedAgent.firstname || ''} ${designatedAgent.lastname || ''}`
      : 'N/A';
  const riderPhone = designatedAgent?.phone || 'N/A';
  const [, setIsModalpending] = useState(false);
  const riderOptions: any = [];
  return (
    <tr className="text-left">
      <td colSpan={5} className="p-5 text-elr-black">
        <div className="flex flex-col gap-4">
          <div className="flex justify-between align-middle items-center">
            <div className="space-y-5 relative">
              <div className="flex items-center">
                <div className="inline-block w-[8px] h-[8px] rounded-full bg-elr-purple" />
                <p className="ml-3">{pickupAddress?.fullAddress || 'N/A'}</p>
              </div>
              <div className="absolute left-1 top-0 bottom-0 border-l-2 border-dashed h-6 border-elr-gray-200" />
              <div className="flex items-start">
                <span className="w-[8px] h-[8px] bg-elr-yellow rounded-sm mt-1.5" />
                <p className="ml-3">
                  {recipientInfo?.address.fullAddress || 'N/A'}
                </p>
              </div>
            </div>
          </div>
          <div className="flex w-full p-8 bg-elr-gray md:gap-40 gap-12">
            <div className="flex flex-col">
              <p>List of Items</p>
              <p className="text-elr-black text-opacity-60 py-1">
                {senderInformation?.data.name || 'N/A'}
              </p>
            </div>
            <div className="flex flex-col">
              <p>Store Details</p>
              <p className="text-elr-black text-opacity-60 py-1">
                {recipientInfo?.name || 'N/A'}
              </p>
              <p className="text-elr-black text-opacity-60">
                {recipientInfo?.phone || 'N/A'}
              </p>
            </div>
            <div className="flex flex-col">
              <p>Customer Details</p>
              <p className="text-elr-black text-opacity-60 py-1">{riderName}</p>
              <p className="text-elr-black text-opacity-60">{riderPhone}</p>
            </div>
            {activeTab === TabNames.NEW_ORDERS && (
              <div className="flex flex-col gap-2 items-end">
                <div className="flex flex-col">
                  <ElrButtonDropdown
                    placeholder="Select Rider"
                    buttonDesign="bg-white rounded-md w-72 h-12"
                    optionsList={riderOptions}
                  />
                </div>
                <ElrButton
                  text="Assign Rider"
                  size="sm"
                  className="text-white bg-elr-black px-7 h-10 rounded-full w-fit"
                  onClick={() => setIsModalpending(true)}
                />
              </div>
            )}
            {(activeTab === TabNames.ONGOING ||
              activeTab === TabNames.COMPLETED) && (
              <div className="flex flex-col gap-1 justify-start">
                <div className="flex flex-col">
                  <p>Tracking ID</p>
                  <p className="text-elr-black text-opacity-60 py-1">
                    {recipientInfo?.trackingId || 'N/A'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};
